import type { LicenseStatus } from './license-types'

const MS_PER_MINUTE = 60 * 1000
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR

function formatUnit(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`
}

function getTimeLabelFromRemainingMs(remainingMs: number): string {
  if (remainingMs < MS_PER_MINUTE) {
    return 'less than 1 minute'
  }

  const daysRemaining = Math.ceil(remainingMs / MS_PER_DAY)
  if (daysRemaining > 1) {
    return formatUnit(daysRemaining, 'day')
  }

  const hoursRemaining = Math.ceil(remainingMs / MS_PER_HOUR)
  if (hoursRemaining >= 2) {
    return formatUnit(hoursRemaining, 'hour')
  }

  const minutesRemaining = Math.ceil(remainingMs / MS_PER_MINUTE)
  if (minutesRemaining >= 2) {
    return formatUnit(minutesRemaining, 'minute')
  }

  return 'less than 1 minute'
}

export interface ExpiryBannerModel {
  prefix: string
  timeLabel: string
  contactName: string
}

/**
 * Returns the expiry banner model for the active license, or null if no banner should be shown.
 * The banner only appears within the grace period window defined by the trial config.
 */
export function getExpiryBannerModel(
  licenseStatus: LicenseStatus | null,
  nowMs = Date.now()
): ExpiryBannerModel | null {
  if (!licenseStatus?.isValid) return null

  const licenseEndMs = Date.parse(licenseStatus.license?.expiresAt ?? '')
  if (!Number.isFinite(licenseEndMs)) return null

  const remainingMs = licenseEndMs - nowMs
  const daysRemaining = remainingMs / MS_PER_DAY

  if (daysRemaining > licenseStatus.gracePeriodDays) return null

  return {
    prefix: 'License expires in',
    timeLabel: getTimeLabelFromRemainingMs(Math.max(0, remainingMs)),
    contactName: licenseStatus.contactName || 'your Accenture representative',
  }
}
